import { useCallback, useMemo } from "react";

import { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { TextTemplate } from "services/types/text-templates.type";

import {
  UseTemplatePickerResult,
  useTemplatePicker
} from "./use-template-picker.hook";

type UseMergedTemplatePickerArgs = {
  templateTypes?: number[];
  search: string;
  /** Which backend(s) to read from. Omitted ⇒ "default" (main backend only). */
  templateSource?: TemplateSourceType;
};

/**
 * On Leads surfaces, merges the native Suno Comms (Leads) templates with the
 * customer's eligible main-app templates so both are pickable in one place
 * (SN-18785). Composes two `useTemplatePicker` instances — one per backend —
 * and reuses their server-side search/pagination as-is; the Comms instance
 * stays dormant on non-Leads surfaces, which then behave exactly as before.
 *
 * The returned list interleaves nothing: Leads templates come first, then
 * main-app. Downstream, the picker splits by `getTemplateSource` (id shape) to
 * group them and to route content resolution to the right backend.
 */
export const useMergedTemplatePicker = ({
  templateTypes,
  search,
  templateSource = "default"
}: UseMergedTemplatePickerArgs): UseTemplatePickerResult => {
  const isLeads = templateSource === "leads";

  const defaultPicker = useTemplatePicker({
    templateTypes,
    search,
    templateSource: "default",
    enabled: true
  });
  const leadsPicker = useTemplatePicker({
    templateTypes,
    search,
    templateSource: "leads",
    enabled: isLeads
  });

  const templates = useMemo<TextTemplate[]>(
    () =>
      isLeads
        ? [...leadsPicker.templates, ...defaultPicker.templates]
        : defaultPicker.templates,
    [isLeads, leadsPicker.templates, defaultPicker.templates]
  );

  const totalCount = isLeads
    ? (leadsPicker.totalCount ?? 0) + (defaultPicker.totalCount ?? 0)
    : defaultPicker.totalCount;

  const hasMore = isLeads
    ? leadsPicker.hasMore || defaultPicker.hasMore
    : defaultPicker.hasMore;

  const isLoading = isLeads
    ? leadsPicker.isLoading || defaultPicker.isLoading
    : defaultPicker.isLoading;

  const isLoadingMore = isLeads
    ? leadsPicker.isLoadingMore || defaultPicker.isLoadingMore
    : defaultPicker.isLoadingMore;

  const loadMore = useCallback(() => {
    if (defaultPicker.hasMore) {
      defaultPicker.loadMore();
    }
    if (isLeads && leadsPicker.hasMore) {
      leadsPicker.loadMore();
    }
  }, [isLeads, defaultPicker, leadsPicker]);

  return { templates, totalCount, hasMore, isLoading, isLoadingMore, loadMore };
};
